/**
 * Privacy-first website analytics abstraction.
 *
 * Deliberately small and provider-agnostic. The goal is *aggregate* public
 * page-view measurement (which marketing pages get visited) WITHOUT cookies,
 * user IDs, fingerprinting, session replay or ad/retargeting pixels.
 *
 * Two safety gates, enforced together, decide whether any analytics script may
 * load at all:
 *
 *   1. Provider must be explicitly opted in via NEXT_PUBLIC_ANALYTICS_PROVIDER.
 *      The default is "none" — analytics is OFF unless someone turns it on.
 *   2. The request host must be an indexable PRODUCTION host
 *      (fixcontrol.ai / www.fixcontrol.ai) AND NODE_ENV must be "production".
 *      Dev, staging, preview deploys and localhost can therefore NEVER send a
 *      single analytics event, regardless of how the env is set.
 *
 * Host indexability reuses the single source of truth in `@/lib/seo`
 * (`isIndexableHost`), so this stays in lockstep with the SEO noindex policy
 * and never drifts. Keep this file dependency-free and side-effect-free so the
 * server component, the client component and the smoke test can all import it.
 *
 * This file MUST NOT touch auth, routing or governance.
 */
import { isIndexableHost } from "@/lib/seo";

export type AnalyticsProvider = "none" | "plausible" | "simple" | "umami";

/**
 * Resolved provider, read from NEXT_PUBLIC_ANALYTICS_PROVIDER. Anything other
 * than an explicitly supported, privacy-friendly provider falls back to
 * "none" — the safe default that renders no script and sends no events.
 */
export function getAnalyticsProvider(): AnalyticsProvider {
  const raw = process.env.NEXT_PUBLIC_ANALYTICS_PROVIDER?.toLowerCase();
  if (raw === "plausible" || raw === "simple" || raw === "umami") return raw;
  return "none";
}

/**
 * The master gate. Returns true ONLY in a production build served from an
 * indexable production host. Everything else — dev/staging/preview hosts,
 * localhost, any non-production NODE_ENV — returns false so analytics never
 * loads and never measures.
 */
export function shouldLoadAnalytics(host: string | null | undefined): boolean {
  return process.env.NODE_ENV === "production" && isIndexableHost(host);
}

/** Public analytics domain for the page-view attribution (Plausible et al.). */
export function getAnalyticsDomain(): string {
  return process.env.NEXT_PUBLIC_ANALYTICS_DOMAIN || "fixcontrol.ai";
}

export type AnalyticsScript = {
  /** The external script URL to inject. */
  src: string;
  /** Extra non-PII attributes (e.g. data-domain / data-website-id). */
  attrs: Record<string, string>;
};

/**
 * Pure resolver: given a provider, return the script descriptor to inject, or
 * `null` when the provider is "none" or its required configuration is missing.
 *
 * No provider is hardcoded in a way that activates external tracking on its
 * own — a script is only ever returned once an operator has explicitly chosen
 * the provider via NEXT_PUBLIC_ANALYTICS_PROVIDER (see getAnalyticsProvider).
 * The well-known Plausible URL is used only as a convenience default *after*
 * that opt-in; self-hosting is supported via NEXT_PUBLIC_PLAUSIBLE_SCRIPT_URL.
 *
 * The returned descriptor never carries cookies, user IDs or personal data.
 */
export function resolveAnalyticsScript(
  provider: AnalyticsProvider,
): AnalyticsScript | null {
  switch (provider) {
    case "plausible": {
      // Plausible is cookieless by design. domain → data-domain; the script
      // URL defaults to the official endpoint but can point at a self-host.
      const src =
        process.env.NEXT_PUBLIC_PLAUSIBLE_SCRIPT_URL ||
        "https://plausible.io/js/script.js";
      return { src, attrs: { "data-domain": getAnalyticsDomain() } };
    }
    case "umami": {
      // Umami requires BOTH the website id and the (self-hosted) script URL.
      // Without either, we refuse to load anything.
      const id = process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID;
      const src = process.env.NEXT_PUBLIC_UMAMI_SCRIPT_URL;
      if (!id || !src) return null;
      return { src, attrs: { "data-website-id": id } };
    }
    case "simple": {
      // A generic privacy-first endpoint (e.g. Simple Analytics): load only if
      // a script URL is explicitly configured.
      const src = process.env.NEXT_PUBLIC_SIMPLE_SCRIPT_URL;
      if (!src) return null;
      return { src, attrs: {} };
    }
    case "none":
    default:
      return null;
  }
}
